import React, { FC, useEffect, useState } from 'react';
import {
    Grid,
    Paper,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    TableCell,
    TableRow,
    TableHead,
    Table,
    TableContainer,
    TableBody,
    Button,
    Collapse,
    Box,
    Typography,
} from '@mui/material';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { useTranslation } from 'react-i18next';
import { PaymentQuery } from '../../../../services/query';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import TimeHelper from '../../../../utils/time';
import { PaymentCommand } from '../../../../services/command';
import Swal from 'sweetalert2';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';
import { SCUILoadingLinear } from '../../../../atomic/atoms';
import { CPM } from './customerPaymentMethods.classes';

const AVAILABILITY_STATUS = {
    Passive: 0,
    NotApproved: 1,
    Approved: 2,
} as const;

interface AvailabilityStatusHistoryNote {
    firstName: string;
    operationDate: string;
    status: any;
    note: any;
}

const parseAvailabilityStatusHistory = (items: any[]): AvailabilityStatusHistoryNote[] =>
    items.map((item: any) => ({
        firstName: `${item.firstName} ${item.lastName}`,
        operationDate: TimeHelper.formatDateHour(item.operationDate) || '-',
        status: item.status || '-',
        note: item.note || '-',
    }));

interface IProps {
    datas?: any;
}
export const CustomerPaymentMethods: FC<IProps> = ({ datas }) => {
    const { t } = useTranslation();
    const [paymentMethods, setPaymentMethods] = React.useState([]);
    const [refresh, setRefresh] = useState(false);
    const refreshPayment = () => {
        setRefresh((prevLoading) => !prevLoading);
    };
    const [loading, setLoading] = useState(false);
    const GetPaymentMethods = () => {
        setLoading(true);
        let params = {
            customerId: datas.customer.customerId,
            regionId: JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.regionId?.toString(),
        };
        PaymentQuery.GetCustomerPaymentMethodAvailabilityStatus(params)
            .then((res: any) => {
                if (res) {
                    setPaymentMethods(res.data.customerPaymentMethodAvailabilityStatusDatas);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };
    const buildAvailabilityParams = (paymentMethod: any, status: any, note: string) => ({
        CustomerId: paymentMethod.paymentMethod.customerId,
        AvailabilityStatusTypeId: status,
        ReasonKey: paymentMethod.paymentMethod.paymentMethodKey,
        PaymentMethodCollectorId: paymentMethod.paymentMethod.paymentMethodCollectorId,
        Note: note,
    });
    const UpdatePaymentMethods = async (paymentMethod: any, status: any, note: string) => {
        if (note?.trim().length === 0) {
            Swal.fire({
                icon: 'error',
                title: t('general.emptyFieldControlText'),
                text: t('customerInformation.noteRequired'),
                timer: 2000,
                showConfirmButton: false,
            });
            return;
        }
        const params = buildAvailabilityParams(paymentMethod, status, note);
        PaymentCommand.UpdateCustomerPaymentMethodAvailabilityStatus(params).then((res: any) => {
            refreshPayment();
            if (res === true) {
                Swal.fire(t('general.updated'), ' ', 'success');
            }
        });
    };
    const RemoveAutoBlock = async (paymentMethod: any) => {
        const result = await Swal.fire({
            icon: 'question',
            title: t('customerInformation.removeAutoBlock'),
            text: t('customerInformation.removeAutoBlockConfirm'),
            input: 'textarea',
            inputLabel: t('customerInformation.removeAutoBlockReason'),
            inputPlaceholder: t('customerInformation.removeAutoBlockReasonPlaceholder'),
            showCancelButton: true,
            confirmButtonText: t('customerInformation.removeAutoBlock'),
            cancelButtonText: t('general.cancel'),
            inputValidator: (value) => {
                if (!value?.trim()) {
                    return t('customerInformation.noteRequired');
                }
                return undefined;
            },
        });
        if (!result.isConfirmed) {
            return;
        }
        const params = buildAvailabilityParams(paymentMethod, AVAILABILITY_STATUS.Approved, result.value);
        PaymentCommand.RemoveCustomerPaymentMethodBlock(params).then((res: any) => {
            refreshPayment();
            if (res === true) {
                Swal.fire(t('general.updated'), ' ', 'success');
            }
        });
    };
    useEffect(() => {
        GetPaymentMethods();
    }, [refresh]);
    function Row(paymentMethod: any) {
        const [openC, setOpenC] = useState(false);
        const [note, setNote] = useState(''); // Notunuzun değeri buradan gelmeli
        const [paymentId, setPaymentId] = React.useState(paymentMethod.paymentMethod.availabilityStatusTypeId);
        const [notes, setNotes] = useState<AvailabilityStatusHistoryNote[]>([]);
        const columns: ExtendedGridColDef[] = [
            { field: 'firstName', headerName: 'Operatör', flex: 1 },
            { field: 'operationDate', headerName: t('general.date'), flex: 1 },
            { field: 'status', headerName: t('general.status'), flex: 1 },
            { field: 'note', headerName: t('customerInformation.note'), flex: 1 },
        ];
        const GetNotes = async () => {
            const params = {
                CustomerId: paymentMethod.paymentMethod.customerId,
                PaymentMethodCollectorId: paymentMethod.paymentMethod.paymentMethodCollectorId,
            };
            const res: any = await PaymentCommand.GetCustomerPaymentMethodAvailabilityStatusHistory(params);
            const historyDatas = res?.customerPaymentMethodAvailabilityStatusHistoryDatas;
            if (historyDatas) {
                setNotes(parseAvailabilityStatusHistory(historyDatas));
            }
        };
        useEffect(() => {
            if (openC === true) {
                GetNotes();
            }
        }, [openC]);
        const handleChange = (event: any) => {
            setPaymentId(event.target.value);
        };
        return (
            <Grid
                className={CPM.methodRow}
                size={{
                    sm: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                }}
            >
                <Paper
                    className={CPM.methodCard}
                    sx={{
                        marginBottom: 1.5,
                        borderRadius: '12px',
                        border: '1px solid #e8eaed',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        overflow: 'hidden',
                        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                        '&:hover': {
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                            borderColor: '#b0bec5',
                        },
                    }}
                >
                    <div className={`sideBySideDiv ${CPM.methodTableSection}`}>
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                <TableHead>
                                    <TableRow />
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '250px',
                                                minWidth: '250px',
                                            }}
                                        >
                                            <Typography
                                                className={CPM.fieldPaymentMethodName}
                                                variant="body2"
                                                fontWeight={600}
                                                color="text.primary"
                                            >
                                                {paymentMethod.paymentMethod.paymentMethodName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '180px',
                                                minWidth: '180px',
                                            }}
                                        >
                                            <FormControl className={CPM.fieldStatusControl} fullWidth size="small">
                                                <InputLabel id="demo-simple-select-label">
                                                    {t('customerInformation.status')}
                                                </InputLabel>
                                                <Select
                                                    className={CPM.inputSelectStatus}
                                                    labelId="demo-simple-select-label"
                                                    id="demo-simple-select"
                                                    value={paymentId}
                                                    label={t('customerInformation.status')}
                                                    onChange={handleChange}
                                                    sx={{ borderRadius: '8px' }}
                                                >
                                                    <MenuItem value={AVAILABILITY_STATUS.Passive}>
                                                        {t('general.passive')}
                                                    </MenuItem>
                                                    <MenuItem value={AVAILABILITY_STATUS.NotApproved}>
                                                        {t('customerInformation.notApproved')}
                                                    </MenuItem>
                                                    <MenuItem value={AVAILABILITY_STATUS.Approved}>
                                                        {t('customerInformation.approved')}
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '400px',
                                            }}
                                        >
                                            <TextField
                                                className={CPM.inputTextfieldNote}
                                                id="outlined-multiline-static"
                                                label={t('customerInformation.note')}
                                                fullWidth
                                                size="small"
                                                value={note}
                                                onChange={(event: any) => {
                                                    setNote(event.target.value);
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '120px',
                                                minWidth: '120px',
                                            }}
                                        >
                                            <Button
                                                className={CPM.inputButtonSave}
                                                variant="contained"
                                                size="small"
                                                disableElevation
                                                onClick={() => UpdatePaymentMethods(paymentMethod, paymentId, note)}
                                                sx={{
                                                    borderRadius: '8px',
                                                    padding: '6px 10px !important',
                                                    fontWeight: 600,
                                                    color: '#ffffff',
                                                    textTransform: 'none',
                                                    minWidth: '90px',
                                                    transition: 'background-color 0.18s ease, box-shadow 0.18s ease',
                                                    '&:hover': {
                                                        boxShadow: '0 2px 8px rgba(25,118,210,0.25)',
                                                    },
                                                }}
                                            >
                                                {t('general.save')}
                                            </Button>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '150px',
                                                minWidth: '150px',
                                            }}
                                        >
                                            {paymentMethod.paymentMethod.availabilityStatusTypeId ===
                                                AVAILABILITY_STATUS.Passive && (
                                                <Button
                                                    className={CPM.inputButtonRemoveAutoBlock}
                                                    variant="outlined"
                                                    color="warning"
                                                    size="small"
                                                    disableElevation
                                                    onClick={() => RemoveAutoBlock(paymentMethod)}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        padding: '6px 10px !important',
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                        minWidth: '120px',
                                                    }}
                                                >
                                                    {t('customerInformation.removeAutoBlock')}
                                                </Button>
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '110px',
                                                minWidth: '110px',
                                            }}
                                        >
                                            <Box
                                                className={CPM.inputToggleHistory}
                                                onClick={() => setOpenC((prev) => !prev)}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.4,
                                                    color: '#1976d2',
                                                    cursor: 'pointer',
                                                    fontWeight: 600,
                                                    fontSize: '0.85rem',
                                                    padding: '4px 10px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #bbdefb',
                                                    transition: 'background-color 0.18s ease',
                                                    userSelect: 'none',
                                                    '&:hover': {
                                                        backgroundColor: '#e3f2fd',
                                                    },
                                                }}
                                            >
                                                {t('general.detail')}
                                                {openC ? (
                                                    <ExpandLess sx={{ fontSize: 18 }} />
                                                ) : (
                                                    <ExpandMore sx={{ fontSize: 18 }} />
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                    <Collapse in={openC}>
                        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                            <Box className={CPM.blockDetailSection} sx={{ mb: 1.5 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                                    {t('customerInformation.blockDetail')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('customerInformation.blockDate')}:{' '}
                                    {TimeHelper.formatDateHour(paymentMethod.paymentMethod.operationDate) || '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('customerInformation.blockRule')}:{' '}
                                    {paymentMethod.paymentMethod.availabilityStatusTypeChangeReasonKey || '-'}
                                </Typography>
                                {(paymentMethod.paymentMethod.firstName ||
                                    paymentMethod.paymentMethod.lastName ||
                                    paymentMethod.paymentMethod.note) && (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('customerInformation.note')}:{' '}
                                        {[
                                            [paymentMethod.paymentMethod.firstName, paymentMethod.paymentMethod.lastName]
                                                .filter(Boolean)
                                                .join(' '),
                                            paymentMethod.paymentMethod.note,
                                        ]
                                            .filter(Boolean)
                                            .join(' — ') || '-'}
                                    </Typography>
                                )}
                            </Box>
                            <Box className={CPM.historyDatagrid}>
                                <SCUICustomDataGrid randomId rows={notes} columns={columns} pageSize={5} />
                            </Box>
                        </Box>
                    </Collapse>
                </Paper>
            </Grid>
        );
    }
    return (
        <Grid className={CPM.page}>
            <Grid
                size={{
                    sm: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                }}
            >
                <SCUILoadingLinear display={loading} />
            </Grid>
            <Grid
                size={{
                    sm: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                }}
            >
                <Paper
                    className={CPM.headerPaper}
                    sx={{
                        marginBottom: 1.5,
                        borderRadius: '12px',
                        border: '1px solid #e0e7ef',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        backgroundColor: '#f4f6f9',
                        overflow: 'hidden',
                    }}
                >
                    <div className={`sideBySideDiv ${CPM.headerTableSection}`}>
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }} aria-label="header table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            className={CPM.headerCellPaymentMethod}
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '250px',
                                                minWidth: '250px',
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                fontWeight={700}
                                                color="text.secondary"
                                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                            >
                                                {t('general.paymentMethod')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            className={CPM.headerCellStatus}
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '180px',
                                                minWidth: '180px',
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                fontWeight={700}
                                                color="text.secondary"
                                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                            >
                                                {t('customerInformation.status')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            className={CPM.headerCellNote}
                                            align="center"
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '400px',
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                fontWeight={700}
                                                color="text.secondary"
                                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                            >
                                                {t('customerInformation.changeNote')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            className={CPM.headerCellSave}
                                            align="center"
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '120px',
                                                minWidth: '120px',
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                fontWeight={700}
                                                color="text.secondary"
                                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                            >
                                                {t('general.save')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            className={CPM.headerCellRemoveAutoBlock}
                                            align="center"
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '150px',
                                                minWidth: '150px',
                                            }}
                                        ></TableCell>
                                        <TableCell
                                            className={CPM.headerCellDetail}
                                            align="center"
                                            style={{
                                                borderBottom: 'none',
                                                textAlign: 'center',
                                                width: '110px',
                                                minWidth: '110px',
                                            }}
                                        ></TableCell>
                                    </TableRow>
                                </TableHead>
                            </Table>
                        </TableContainer>
                    </div>
                </Paper>
            </Grid>
            {paymentMethods.map((row: any) => (
                <Row key={row.customerId} paymentMethod={row} />
            ))}
        </Grid>
    );
};
